import {
  continueAgentChatApiV1AiAgentsChatPost,
  fetchAgentConversationHistoryApiV1AiAgentsPlatformConversationsConversationIdHistoryGet,
  fetchAgentInsightApiV1AiAgentsPlatformInsightPost,
} from "@/backend-sdk/client/sdk.gen";
import type {
  AgentChatResponse as BackendAgentChatResponse,
  AgentConversationHistoryResponse,
  AgentInsightResponse as BackendAgentInsightResponse,
  BodyContinueAgentChatApiV1AiAgentsChatPost,
} from "@/backend-sdk/client/types.gen";

export type AgentSource = "google-ads" | "meta-ads" | "google-analytics";

export interface AgentInsightResponse {
  source: AgentSource;
  message: string;
  conversationId?: string;
  userId?: string;
  startedAt?: string;
  filters: {
    startDate: string;
    endDate: string;
    previousStartDate: string;
    previousEndDate: string;
  };
}

export interface AgentChatMessage {
  role: "user" | "assistant";
  content: string;
}

interface AgentChatPayload {
  source: AgentSource;
  insight: string;
  userMessage: string;
  history: AgentChatMessage[];
  filters: AgentInsightResponse["filters"];
  conversationId?: string;
  attachments?: File[];
}

type AgentChatRequestBody = BodyContinueAgentChatApiV1AiAgentsChatPost;
type AgentChatSdkResponse = BackendAgentChatResponse;

interface AgentChatResponse {
  response: AgentChatSdkResponse["response"];
  source: AgentSource;
  conversationId?: string;
  attachments?: Array<{
    name: string;
    contentType: string;
    size: string;
    snippet: string;
  }>;
}

export interface AgentHistoryMessage {
  role: "user" | "assistant";
  content: string;
  createdAt: string;
}

interface AgentHistoryPagination {
  totalItems: number;
  currentPage: number;
  totalPages: number;
  pageSize: number;
}

interface AgentHistoryResponse {
  conversationId: string;
  messages: AgentHistoryMessage[];
  pagination: AgentHistoryPagination;
}

interface FetchInsightParams {
  startDate: string;
  endDate: string;
  previousStartDate: string;
  previousEndDate: string;
  accountId?: string;
  signal?: AbortSignal;
}

class AiAgentsService {
  async fetchInsight(
    source: AgentSource,
    params: FetchInsightParams,
  ): Promise<AgentInsightResponse> {
    const { data } = await fetchAgentInsightApiV1AiAgentsPlatformInsightPost({
      path: { platform: source },
      query: {
        start_date: params.startDate,
        end_date: params.endDate,
        previous_start_date: params.previousStartDate,
        previous_end_date: params.previousEndDate,
        account_id: params.accountId,
      },
      signal: params.signal,
      throwOnError: true,
    });

    const insightData: BackendAgentInsightResponse = data;

    return {
      source: insightData.source as AgentSource,
      message:
        typeof insightData.message === "string" ? insightData.message : "",
      conversationId: insightData.conversation_id ?? undefined,
      userId: insightData.user_id ?? undefined,
      startedAt: insightData.started_at ?? undefined,
      filters: {
        startDate: insightData.filters.start_date,
        endDate: insightData.filters.end_date,
        previousStartDate: insightData.filters.previous_start_date,
        previousEndDate: insightData.filters.previous_end_date,
      },
    };
  }

  async sendMessage(payload: AgentChatPayload): Promise<AgentChatResponse> {
    const body: AgentChatRequestBody = {
      source: payload.source,
      insight: payload.insight,
      user_message: payload.userMessage,
      chat_history: JSON.stringify(payload.history),
      filters: JSON.stringify(payload.filters),
      conversation_id: payload.conversationId,
      files: payload.attachments ?? null,
    };

    const { data } = await continueAgentChatApiV1AiAgentsChatPost({
      body,
      headers: {
        "x-ignore-global-filters": "true",
      },
      throwOnError: true,
    });

    return {
      response: data.response,
      source: data.source as AgentSource,
      conversationId: data.conversation_id ?? undefined,
      attachments: data.attachments.map((attachment) => ({
        name: attachment.name,
        contentType: attachment.content_type,
        size: attachment.size,
        snippet: attachment.snippet,
      })),
    };
  }

  async fetchHistory(
    source: AgentSource,
    conversationId: string,
    page = 1,
    limit = 50,
  ): Promise<AgentHistoryResponse> {
    const { data } =
      await fetchAgentConversationHistoryApiV1AiAgentsPlatformConversationsConversationIdHistoryGet(
        {
          path: { platform: source, conversation_id: conversationId },
          query: { page, limit },
          throwOnError: true,
        },
      );

    const historyData: AgentConversationHistoryResponse = data;

    const rawMessages = historyData.messages ?? [];
    const messages: AgentHistoryMessage[] = rawMessages.map((message) => {
      const normalizedRole: AgentHistoryMessage["role"] =
        message.role === "assistant" || message.role === "system"
          ? "assistant"
          : "user";

      return {
        role: normalizedRole,
        content: message.content ?? "",
        createdAt: message.created_at ?? new Date().toISOString(),
      };
    });

    return {
      conversationId: historyData.conversation_id || conversationId,
      messages,
      pagination: {
        totalItems: Number(historyData.pagination?.total ?? messages.length),
        currentPage: Number(historyData.pagination?.page ?? page),
        totalPages: Number(historyData.pagination?.total_pages ?? 1),
        pageSize: Number(historyData.pagination?.limit ?? limit),
      },
    };
  }
}

export const aiAgentsService = new AiAgentsService();
